import {
  Box,
  Flex,
  Text,
  Grid,
  GridItem,
  IconButton,
  Tooltip,
  Badge,
  VStack,
  HStack,
  Button,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import { useRecoilValue } from "recoil";
import { customColors } from "../../../atoms/customColors";
import moment from "moment";
import "moment/locale/pt-br";
import {
  FaChevronLeft,
  FaChevronRight,
  FaEye,
  FaArrowLeft,
} from "react-icons/fa";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ListaBoletoAPI from "../../../services/ListaBoleto";
import jwt_decode from "jwt-decode";

interface CalendarItem {
  dataVencimento: string;
  qtdePrevista: number;
  qtdeRealizada: number;
  valorPrevisto: number;
  valorRealizado: number;
}

interface JWTToken {
  idCliente: number;
}

export default function CalendarioBoletos() {
  const colors = useRecoilValue(customColors);
  const [currentDate, setCurrentDate] = useState(moment());
  const [calendarData, setCalendarData] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDay, setSelectedDay] = useState<moment.Moment | null>(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const jwtToken: JWTToken = token ? jwt_decode(token) : { idCliente: 0 };
  const idCliente = jwtToken.idCliente;

  const startOfMonth = currentDate.clone().startOf("month");
  const endOfMonth = currentDate.clone().endOf("month");
  const startDate = startOfMonth.clone().startOf("week");
  const endDate = endOfMonth.clone().endOf("week");

  const fetchCalendarData = async () => {
    if (!idCliente) return;
    setLoading(true);
    try {
      const dataInicio = startOfMonth.format("YYYY-MM-DD");
      const dataFim = endOfMonth.format("YYYY-MM-DD");

      const response = await ListaBoletoAPI.getCalendarData(
        dataInicio,
        dataFim,
        idCliente
      );
      setCalendarData(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar calendário:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate, idCliente]);

  const getDayData = (date: moment.Moment): CalendarItem | undefined => {
    const dateStr = date.format("YYYY-MM-DD");
    return calendarData.find((item) => item.dataVencimento.startsWith(dateStr));
  };

  const getTotalDoMes = () => {
    return calendarData.reduce(
      (acc, item) => ({
        previsto: acc.previsto + item.valorPrevisto,
        realizado: acc.realizado + item.valorRealizado,
      }),
      { previsto: 0, realizado: 0 }
    );
  };

  const prevMonth = () =>
    setCurrentDate(currentDate.clone().subtract(1, "month"));
  const nextMonth = () => setCurrentDate(currentDate.clone().add(1, "month"));

  const days: moment.Moment[] = [];
  let day = startDate.clone();
  while (day.isBefore(endDate) || day.isSame(endDate, "day")) {
    days.push(day.clone());
    day.add(1, "day");
  }

  const openModal = (day: moment.Moment) => {
    setSelectedDay(day);
    onOpen();
  };

  const selectedDayData = selectedDay ? getDayData(selectedDay) : null;
  const totalMes = getTotalDoMes();

  return (
    <>
      <Flex
        flexDir="column"
        height="85vh"
        width="100%"
        bgColor={colors.GRAY_LIGHTEST}
        p={8}
        overflow="hidden"
      >
        <Box
          bg={colors.WHITE}
          borderRadius="lg"
          boxShadow="md"
          p={6}
          flex="1"
          overflow="hidden"
          display="flex"
          flexDir="column"
        >
          <Flex alignItems="center" justifyContent="space-between" mb={6}>
            <Flex alignItems="center" gap={3}>
              <IconButton
                aria-label="Mês anterior"
                icon={<FaChevronLeft />}
                onClick={prevMonth}
                bgColor={colors.BLUEMONEDDBUTTONS}
                color={colors.WHITE}
                size="md"
                _hover={{ bgColor: colors.BLUEMONEDDRGB2 }}
                _active={{ bgColor: colors.BLUEMONEDDBACKGROUND }}
                borderRadius="full"
              />
              <Text
                fontSize="2xl"
                fontWeight="bold"
                color={colors.BLUEMONEDDBACKGROUND}
              >
                {currentDate.format("MMMM YYYY")}
              </Text>
              <IconButton
                aria-label="Próximo mês"
                icon={<FaChevronRight />}
                onClick={nextMonth}
                bgColor={colors.BLUEMONEDDBUTTONS}
                color={colors.WHITE}
                size="md"
                _hover={{ bgColor: colors.BLUEMONEDDRGB2 }}
                _active={{ bgColor: colors.BLUEMONEDDBACKGROUND }}
                borderRadius="full"
              />
            </Flex>

            <HStack spacing={4}>
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color={colors.GRAY_DARK}>
                  Previsto:
                </Text>
                <Text fontWeight="bold" color={colors.BLUEMONEDDBACKGROUND}>
                  {totalMes.previsto.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </Text>
              </VStack>
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color={colors.GRAY_DARK}>
                  Realizado:
                </Text>
                <Text fontWeight="bold" color={colors.GREENMONEDD}>
                  {totalMes.realizado.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </Text>
              </VStack>
              <Button
                bgColor="transparent"
                color={colors.BLUEMONEDDBUTTONS}
                size="md"
                onClick={() => navigate("/vendas/bolepix")}
                leftIcon={<FaArrowLeft />}
                _hover={{ color: colors.BLUEMONEDDRGB2 }}
                transition="all 0.2s"
                fontWeight="medium"
              >
                Voltar
              </Button>
            </HStack>
          </Flex>

          {loading ? (
            <Flex flex="1" align="center" justify="center">
              <Spinner
                size="xl"
                color={colors.BLUEMONEDDBUTTONS}
                thickness="4px"
              />
            </Flex>
          ) : (
            <Box flex="1" overflow="auto">
              <Grid templateColumns="repeat(7, 1fr)" gap={2} mb={2}>
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map(
                  (dia) => (
                    <GridItem key={dia}>
                      <Text
                        textAlign="center"
                        fontWeight="bold"
                        color={colors.BLUEMONEDDBACKGROUND}
                        fontSize="sm"
                        py={2}
                      >
                        {dia}
                      </Text>
                    </GridItem>
                  )
                )}
              </Grid>

              <Grid templateColumns="repeat(7, 1fr)" gap={3}>
                {days.map((day, idx) => {
                  const dayData = getDayData(day);
                  const isCurrentMonth = day.isSame(currentDate, "month");
                  const isToday = day.isSame(moment(), "day");
                  const hasData =
                    dayData &&
                    (dayData.qtdePrevista > 0 || dayData.qtdeRealizada > 0);

                  return (
                    <GridItem
                      key={idx}
                      minH="140px"
                      bg={isCurrentMonth ? colors.WHITE : colors.GRAY_LIGHTEST}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor={
                        isToday ? colors.BLUEMONEDDBUTTONS : "transparent"
                      }
                      p={2}
                      transition="all 0.2s"
                      _hover={{
                        boxShadow: isCurrentMonth ? "lg" : "none",
                        transform: isCurrentMonth ? "translateY(-2px)" : "none",
                      }}
                      cursor={isCurrentMonth && hasData ? "pointer" : "default"}
                      opacity={isCurrentMonth ? 1 : 0.5}
                      onClick={() =>
                        isCurrentMonth && hasData && openModal(day)
                      }
                    >
                      <VStack align="stretch" spacing={1} h="100%">
                        <Flex justify="space-between" align="center">
                          <Text
                            fontWeight={isToday ? "bold" : "medium"}
                            fontSize="sm"
                            color={
                              isToday
                                ? colors.BLUEMONEDDBACKGROUND
                                : day.day() === 0 || day.day() === 6
                                ? colors.GRAY_DARK
                                : colors.GRAY_DARKER
                            }
                          >
                            {day.format("D")}
                          </Text>
                          {hasData && (
                            <Badge
                              colorScheme="blue"
                              variant="solid"
                              fontSize="2xs"
                              px={2}
                              borderRadius="full"
                            >
                              {dayData!.qtdePrevista + dayData!.qtdeRealizada}
                            </Badge>
                          )}
                        </Flex>

                        {hasData && (
                          <>
                            <Divider borderColor={colors.GRAY_LIGHTER} />
                            <VStack align="stretch" spacing={1} flex="1">
                              {dayData!.qtdePrevista > 0 && (
                                <HStack fontSize="xs" color={colors.GRAY_DARK}>
                                  <Text flex="1">Previsto</Text>
                                  <Text
                                    fontWeight="bold"
                                    color={colors.BLUEMONEDDBACKGROUND}
                                  >
                                    {dayData!.valorPrevisto.toLocaleString(
                                      "pt-BR",
                                      { style: "currency", currency: "BRL" }
                                    )}
                                  </Text>
                                </HStack>
                              )}
                              {dayData!.qtdeRealizada > 0 && (
                                <HStack
                                  fontSize="xs"
                                  color={colors.GREENMONEDD}
                                >
                                  <Text flex="1">Realizado</Text>
                                  <Text fontWeight="bold">
                                    {dayData!.valorRealizado.toLocaleString(
                                      "pt-BR",
                                      { style: "currency", currency: "BRL" }
                                    )}
                                  </Text>
                                </HStack>
                              )}
                            </VStack>
                            <Divider borderColor={colors.GRAY_LIGHTER} />
                            <Flex justify="space-between" align="center">
                              <Text
                                fontSize="xs"
                                color={colors.GRAY_DARK}
                                fontWeight="medium"
                              >
                                Total:
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color={colors.BLUEMONEDDBACKGROUND}
                              >
                                {(
                                  dayData!.valorPrevisto +
                                  dayData!.valorRealizado
                                ).toLocaleString("pt-BR", {
                                  style: "currency",
                                  currency: "BRL",
                                })}
                              </Text>
                            </Flex>
                          </>
                        )}
                      </VStack>
                    </GridItem>
                  );
                })}
              </Grid>
            </Box>
          )}

          <Flex justify="center" mt={6} gap={6}>
            <HStack>
              <Box
                w="12px"
                h="12px"
                borderRadius="full"
                bg={colors.YELLOWMONEDD}
              />
              <Text fontSize="sm" color={colors.GRAY_DARK}>
                Pendente
              </Text>
            </HStack>
            <HStack>
              <Box
                w="12px"
                h="12px"
                borderRadius="full"
                bg={colors.GREENMONEDD}
              />
              <Text fontSize="sm" color={colors.GRAY_DARK}>
                Pago
              </Text>
            </HStack>
            <HStack>
              <Box
                w="12px"
                h="12px"
                borderRadius="full"
                bg={colors.ORANGEMONEDD}
              />
              <Text fontSize="sm" color={colors.GRAY_DARK}>
                Falhou
              </Text>
            </HStack>
            <HStack>
              <Box w="12px" h="12px" borderRadius="full" bg={colors.GRAYCARD} />
              <Text fontSize="sm" color={colors.GRAY_DARK}>
                Nova
              </Text>
            </HStack>
            <HStack>
              <Box w="12px" h="12px" borderRadius="full" bg={colors.REDCARD} />
              <Text fontSize="sm" color={colors.GRAY_DARK}>
                Chargeback
              </Text>
            </HStack>
            <HStack>
              <Box
                w="12px"
                h="12px"
                borderRadius="full"
                bg={colors.REDMONEDD}
              />
              <Text fontSize="sm" color={colors.GRAY_DARK}>
                Cancelado
              </Text>
            </HStack>
          </Flex>
        </Box>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg={colors.WHITE} borderRadius="lg" boxShadow="xl">
          <ModalHeader
            bg={colors.BLUEMONEDDBACKGROUND}
            color={colors.WHITE}
            borderTopRadius="lg"
            py={4}
            textAlign="center"
          >
            <Flex align="center" justify="center" gap={2}>
              <Icon as={FaEye} />
              <Text fontWeight="bold">
                Detalhes - {selectedDay?.format("DD [de] MMMM YYYY")}
              </Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton color={colors.WHITE} />
          <ModalBody p={6}>
            {selectedDayData ? (
              <VStack align="stretch" spacing={3}>
                <Box p={3} bg={colors.GRAY_LIGHTEST} borderRadius="md">
                  <Text fontSize="sm" color={colors.GRAY_DARK}>
                    Boletos Previstos
                  </Text>
                  <Text
                    fontWeight="bold"
                    color={colors.BLUEMONEDDBACKGROUND}
                    fontSize="lg"
                  >
                    {selectedDayData.qtdePrevista} ×{" "}
                    {selectedDayData.valorPrevisto.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </Text>
                </Box>
                <Box p={3} bg={colors.GRAY_LIGHTEST} borderRadius="md">
                  <Text fontSize="sm" color={colors.GRAY_DARK}>
                    Boletos Realizados
                  </Text>
                  <Text
                    fontWeight="bold"
                    color={colors.GREENMONEDD}
                    fontSize="lg"
                  >
                    {selectedDayData.qtdeRealizada} ×{" "}
                    {selectedDayData.valorRealizado.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </Text>
                </Box>
                <Divider />
                <Flex
                  justify="space-between"
                  p={2}
                  bg={colors.GRAY_LIGHTEST}
                  borderRadius="md"
                >
                  <Text fontWeight="bold" color={colors.BLUEMONEDDBACKGROUND}>
                    Total do Dia
                  </Text>
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color={colors.BLUEMONEDDBACKGROUND}
                  >
                    {(
                      selectedDayData.valorPrevisto +
                      selectedDayData.valorRealizado
                    ).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </Text>
                </Flex>
              </VStack>
            ) : (
              <Text textAlign="center" color={colors.GRAY_DARK}>
                Nenhum dado para este dia.
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
